import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';
// =====================================================================
// TODO: Replace YOUR_FORM_ID with your actual Formspree form ID
// 1. Sign up at https://formspree.io
// 2. Create a new form, verify your email
// 3. Copy the form ID (looks like 'xyzabcde' or 'mldwbpra')
// 4. Paste it below
// =====================================================================
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xkoyvnke';
const ACCENT = '#3B4A4F';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Contact() {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    // Honeypot spam check — if the hidden field is filled, it's a bot
    if (formData.get('_gotcha')) {
      setStatus('success'); // pretend success to fool bots
      return;
    }
    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        const data = await response.json().catch(() => ({}));
        setErrorMessage(data?.error || 'Something went wrong. Please try again.');
        setStatus('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2190">
      {/* HERO */}
      <section className="relative pt-32 md:pt-36 pb-16 md:pb-24 px-6 md:px-10 bg-cover bg-center bg-no-repeat" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/wYTUiK2SU4DWFQWfeVY3zi/file_00000000c0ac71f69ffd0e1784397927.png')"
    }} data-id="element-2191">
        {/* Soft overlay to keep text legible */}
        <div className="absolute inset-0 bg-[#F9F9F9]/55 pointer-events-none" data-id="element-2192" />

        <div className="relative max-w-4xl mx-auto text-center" data-id="element-2193">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-2194">
            <motion.p variants={fadeUpVariants} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
            color: '#3B4A4F'
          }} data-id="element-2195">
              Get In Touch
            </motion.p>
            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-[#1F1F1F] leading-[1.05] mb-2" data-id="element-2196">
              Let's build your structure.
            </motion.h1>
            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif italic leading-[1.05] mb-7" style={{
            color: '#3B4A4F'
          }} data-id="element-2197">
              Together.
            </motion.h1>
            <motion.div variants={fadeUpVariants} className="w-14 h-px bg-[#1F1F1F]/30 mb-8" data-id="element-2198" />
            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#3A3A33] max-w-2xl leading-relaxed" data-id="element-2199">
              Whether you're starting GLP-1 therapy, navigating side effects, or
              planning to taper off, the Structured Nutrition team would love to
              hear from you. Send a note and we'll respond within 48 hours.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FORM SECTION */}
      <section className="bg-[#F9F9F9] py-20 md:py-28 px-6" data-id="element-2200">
        <div className="max-w-3xl mx-auto" data-id="element-2201">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} data-id="element-2202">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] mb-3" style={{
            color: '#3B4A4F'
          }} data-id="element-2203">
              Send a Message
            </p>
            <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-10" data-id="element-2204">
              Tell us a bit about yourself.
            </h2>

            {status === 'success' ? <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="bg-white border-l-4 border-tealslate p-8 flex gap-5" data-id="element-2205">
                <CheckCircle2 className="w-8 h-8 text-tealslate shrink-0" data-id="element-2206" />
                <div data-id="element-2207">
                  <h3 className="text-2xl font-serif text-[#1F1F1F] mb-2" data-id="element-2208">
                    Message sent.
                  </h3>
                  <p className="text-[#3A3A33] leading-relaxed mb-4" data-id="element-2209">
                    Thanks for reaching out — we'll get back to you within 48
                    hours. Keep an eye on your inbox (including the spam folder,
                    just in case).
                  </p>
                  <button onClick={() => setStatus('idle')} className="text-[11px] font-bold text-tealslate uppercase tracking-[0.2em] hover:text-[#2A3A3F] transition-colors inline-flex items-center gap-2" data-id="element-2210">
                    Send another message <ArrowRight className="w-3 h-3" data-id="element-2211" />
                  </button>
                </div>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2212">
                {/* Honeypot — hidden, bots will fill it */}
                <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px'
            }} aria-hidden="true" data-id="element-2213" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2214">
                  <div data-id="element-2215">
                    <label htmlFor="name" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2216">
                      Full Name *
                    </label>
                    <input id="name" name="name" type="text" required className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2217" />
                  </div>
                  <div data-id="element-2218">
                    <label htmlFor="email" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2219">
                      Email *
                    </label>
                    <input id="email" name="email" type="email" required className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2220" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2221">
                  <div data-id="element-2222">
                    <label htmlFor="phone" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2223">
                      Phone{' '}
                      <span className="text-[#5A5A50] font-normal normal-case tracking-normal" data-id="element-2224">
                        (optional)
                      </span>
                    </label>
                    <input id="phone" name="phone" type="tel" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2225" />
                  </div>
                  <div data-id="element-2226">
                    <label htmlFor="interest" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2227">
                      I'm Interested In *
                    </label>
                    <select id="interest" name="interest" required defaultValue="" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all" data-id="element-2228">
                      <option value="" disabled data-id="element-2229">
                        Select one…
                      </option>
                      <option value="GLP-1 Nutrition Coaching" data-id="element-2230">
                        1:1 GLP-1 Nutrition Coaching
                      </option>
                      <option value="Body Composition & Muscle" data-id="element-2231">
                        Body Composition & Muscle
                      </option>
                      <option value="Transitioning Off Medication" data-id="element-2232">
                        Transitioning Off Medication
                      </option>
                      <option value="Supplement Recommendations" data-id="element-2233">
                        Supplement Recommendations
                      </option>
                      <option value="General Inquiry" data-id="element-2234">General Inquiry</option>
                      <option value="Press / Speaking" data-id="element-2235">Press / Speaking</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-2236">
                  <label htmlFor="message" className="block text-[10px] font-bold text-[#1F1F1F] uppercase tracking-[0.2em] mb-3" data-id="element-2237">
                    Message *
                  </label>
                  <textarea id="message" name="message" required rows={6} placeholder="Tell us about your goals and where you are on your journey…" className="w-full bg-white border border-[#D8D8D0] px-5 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate focus:ring-1 focus:ring-tealslate transition-all resize-none" data-id="element-2238" />
                </div>

                {status === 'error' && <div className="bg-red-50 border-l-4 border-red-600 p-4 flex gap-3 items-start" data-id="element-2239">
                    <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" data-id="element-2240" />
                    <p className="text-sm text-red-800" data-id="element-2241">{errorMessage}</p>
                  </div>}

                <div className="pt-2" data-id="element-2242">
                  <button type="submit" disabled={status === 'submitting'} className="px-10 py-4 bg-tealslate text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#2A3A3F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-3" data-id="element-2243">
                    {status === 'submitting' ? <>
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2244" />
                        Sending…
                      </> : <>
                        Send Message <ArrowRight className="w-4 h-4" data-id="element-2245" />
                      </>}
                  </button>
                </div>

                <p className="text-xs text-[#5A5A50] italic pt-4" data-id="element-2246">
                  Your information stays private. We respond personally to every
                  message.
                </p>
              </form>}
          </motion.div>
        </div>
      </section>
    </div>;
}